import {
  TimeProgramSiteStatus,
  SavedSearchParamsDtoType,
  TimePrescreeningSavedSearchResponseDtoType,
  TimePrescreeningUpsertSavedSearchRequestDtoType,
} from '@tempus/trials-types';
import { AxiosResponse } from 'axios';

import { TrialSearchQuery, TrialSearchResult } from '~/store/trialSearch/state';
import { BaseTrial, ExternalTrial } from '~/store/trial/types';

import axios from '../axios';
import { TRIALS_API_ROOT, V1_TRIALS, V3_TIME_PRESCREENING } from '../constants';
import { GetTrialsBySiteRequest, TrialResponse } from '../types/portal';
import { TrialForDropdown } from '../types/timeScreeningStore';

export const trialsApi = {
  getAll: async (): Promise<Omit<BaseTrial, 'cohorts'>[]> => {
    return axios
      .get(`${TRIALS_API_ROOT}/trials`, {
        params: {
          attributes: 'id,nctId,title,shortName,sponsor,tempusTrialId',
          bitOperationQuery: 'program_types=1,program_types=2',
          bitOperationOperator: 'OR',
          include: 'drug',
          'include.drug.attributes': 'name',
          overrideLimit: true,
        },
      })
      .then(({ data }) => data.data);
  },
  getForTimeScreeningDropdown: async (): Promise<TrialForDropdown[]> => {
    return axios
      .get(`${TRIALS_API_ROOT}/trials`, {
        params: {
          attributes: 'id,nctId,shortName,tempusTrialId,title',
          bitOperationQuery: 'program_types=1',
          bitOperationOperator: 'OR',
          overrideLimit: true,
        },
      })
      .then(({ data }) => data.data);
  },
  getSavedTrialFilters: async (trialId: string): Promise<TimePrescreeningSavedSearchResponseDtoType | null> => {
    const { data } = await axios.get(`${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/saved-search`);

    return data ?? null;
  },
  updateSavedTrialFilters: async (trialId: string, params: SavedSearchParamsDtoType): Promise<AxiosResponse<void>> => {
    const payload: TimePrescreeningUpsertSavedSearchRequestDtoType = { params };

    return axios.post(`${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/saved-search`, payload);
  },
  deleteSavedTrialFilters: async (trialId: string): Promise<AxiosResponse<void>> => {
    return axios.delete(`${V3_TIME_PRESCREENING}/trials/${encodeURIComponent(trialId)}/saved-search`);
  },
  getTrial: async (trialId: string): Promise<ExternalTrial> => {
    return axios.get(`${V1_TRIALS}/${trialId}/external`).then(({ data }) => data);
  },
};

export const trialMatchesApi = {
  getTrialMatches: async (
    searchQuery: TrialSearchQuery,
    siteId: string | null,
    statuses: TimeProgramSiteStatus[],
  ): Promise<AxiosResponse<TrialSearchResult[]>> => {
    return axios.post(`${TRIALS_API_ROOT}/v1/trial-matches/sites`, {
      searchQuery,
      siteId,
      statuses,
    });
  },
};

export const trialSitesSearchApi = {
  getTrialsBySite: async (body: GetTrialsBySiteRequest): Promise<{ trials: TrialResponse[]; count: number }> => {
    return axios.post(`${TRIALS_API_ROOT}/v1/trial-sites-search`, body).then(({ data }) => data);
  },
};
